import React from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  Body,
  SecondaryBody,
  ExternalLink,
} from '@grnhse/seedling/lib/birch/typography';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';
import { type CompanyProfile } from 'shared/components/company_info/types';

const t = withLocalePrefix('shared.company_info');

const CARD_WIDTH = 340;

const Card = styled.div`
  width: ${CARD_WIDTH}px;
  padding: 16px;
  background: ${FullPalette.WHITE};
  border: 1px solid ${FullPalette.GREY_700};
  border-radius: 6px;
  box-shadow: 0 4px 12px ${FullPalette.EVERGREEN_700}21;
`;

// Required copy: the dataset is public and name-matched, so it may not describe the
// employer the candidate actually worked for.
const Disclaimer = styled(SecondaryBody)`
  color: ${FullPalette.GREY_800};
`;

// Truncate long URLs so each link keeps its external-link icon on the same line.
const LinkLabel = styled.span`
  display: inline-block;
  max-width: ${CARD_WIDTH - 90}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;

const formatEmployeeCount = (
  employeeCount: CompanyProfile['employee_count']
) => {
  if (!employeeCount) {
    return null;
  }

  const format = (value: number) => value.toLocaleString();

  return employeeCount.max === null
    ? t('employees_min', { min: format(employeeCount.min) })
    : t('employees_range', {
        min: format(employeeCount.min),
        max: format(employeeCount.max),
      });
};

const Fact = ({
  value,
  missing,
}: {
  value: string | null;
  missing: string;
}) => <SecondaryBody metadata>{value ?? missing}</SecondaryBody>;

const LinkFact = ({
  link,
  missing,
}: {
  link: CompanyProfile['website'];
  missing: string;
}) => {
  if (!link) {
    return <SecondaryBody metadata>{missing}</SecondaryBody>;
  }

  return (
    <ExternalLink href={link.url} target="_blank" rel="noopener noreferrer">
      <LinkLabel title={link.label}>{link.label}</LinkLabel>
    </ExternalLink>
  );
};

type Props = {
  companyName: string;
  profile: CompanyProfile | null;
  isLoading: boolean;
};

const CompanyInfoCard = ({ companyName, profile, isLoading }: Props) => (
  <Card data-provides="company-info-card">
    <Stack direction="column" spacing={1}>
      <Body medium>{companyName}</Body>

      {isLoading && <SecondaryBody metadata>{t('loading')}</SecondaryBody>}

      {!isLoading && !profile && (
        <SecondaryBody metadata>{t('not_found')}</SecondaryBody>
      )}

      {!isLoading && profile && (
        <>
          <Stack direction="column" spacing={0}>
            <Fact value={profile.industry} missing={t('industry_missing')} />
            <Fact
              value={formatEmployeeCount(profile.employee_count)}
              missing={t('employees_missing')}
            />
            <Fact
              value={
                profile.founded ? t('founded', { year: profile.founded }) : null
              }
              missing={t('founded_missing')}
            />
            <Fact value={profile.location} missing={t('location_missing')} />
          </Stack>

          <Divider />

          <Stack direction="column" spacing={0.5}>
            <LinkFact link={profile.website} missing={t('website_missing')} />
            <LinkFact link={profile.linkedin} missing={t('linkedin_missing')} />
          </Stack>

          <Disclaimer>{t('disclaimer')}</Disclaimer>
        </>
      )}
    </Stack>
  </Card>
);

export default CompanyInfoCard;
